import { AccessToken, IDToken, SessionObject, TokenResponse } from '@okta/okta-auth-js'
import { useOktaAuth } from '@okta/okta-react'
import { legit } from 'apex-legit-service'
import { ApexAuth, LoginTypePayload, LoginTypeResponse, SsoTokenPayload } from 'apex-ui-services'
import { ApexApiError } from 'apex-ui-services/src/types/CommonTypes'
import isNil from 'lodash.isnil'
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Card, Header } from 'semantic-ui-react'
import { MessageCenter } from '../../components/MessageCenter'
import { ALL_REQUESTED_PERMISSIONS, LEGIT_SERVICE_CONFIG } from '../../constants'
import { RootState } from '../../store'
import layoutStyles from '../AppLayout/styles.module.less'
import { ApexLegacyLogin } from './ApexLegacyLogin'
import { ApexUsernameCheck } from './ApexUsernameCheck'
import { SSO_ERROR_MESSAGE } from './constants'
import { OktaLogin } from './OktaLogin'
import styles from './styles.module.less'
export const isPrimitiveEmpty = (value: string | number | undefined | null) => {
  let empty = false
  if (isNil(value)) {
    empty = true
  } else if ((value as string).length !== undefined && (value as string).trim().length === 0) {
    empty = true
  }
  return empty
}
export interface Props {
  idpCheck: (params: LoginTypePayload) => void
  loginTypeResponse?: LoginTypeResponse | null
  ssoLogin: (params: SsoTokenPayload) => void
  isLegacyLoginLoading: boolean
  ssoLoginIsLoading: boolean
  isLoginTypeLoading: boolean
  adoptLegitSession: (config: Record<string, any>, requestedPermissions: string[]) => void
  failedLegitAuth: boolean
  ssoError?: ApexApiError | null
  clearSsoLogin: () => void
}

export const ApexLoginComponent = ({
  idpCheck,
  loginTypeResponse,
  isLegacyLoginLoading,
  ssoLoginIsLoading,
  isLoginTypeLoading,
  adoptLegitSession,
  ssoLogin,
  failedLegitAuth,
  ssoError,
  clearSsoLogin,
}: Props) => {
  const [username, setUsername] = useState('')
  const [usernameError, setUsernameError] = useState<boolean>(false)
  // eslint-disable-next-line
  const { authState, oktaAuth } = useOktaAuth()

  const retrieveOktaSession = async () => {
    const session: SessionObject = await oktaAuth.session.get()
    if (session.status === 'ACTIVE') {
      let token: TokenResponse
      try {
        token = await oktaAuth.token.getWithoutPrompt()
        console.log('getWithoutPrompt')
      } catch (e) {
        token = await oktaAuth.token.parseFromUrl()
        console.log('parseFromUrl')
      }
      const idToken: IDToken | undefined = token?.tokens?.idToken
      const accessToken: AccessToken | undefined = token?.tokens?.accessToken
      ssoLogin({
        idToken: idToken?.idToken,
        accessToken: accessToken?.accessToken,
      })
    }
  }

  useEffect(() => {
    // Check if there is an existing Okta session when the user lands on the page
    if (failedLegitAuth) {
      if (oktaAuth.token.isLoginRedirect()) {
        oktaAuth.token
          .parseFromUrl()
          .then(() => {
            retrieveOktaSession()
          })
          .catch((err: any) => console.error(err)) // eslint-disable-line no-console
      } else {
        retrieveOktaSession()
      }
    }
    // eslint-disable-next-line
  }, [failedLegitAuth])

  useEffect(() => {
    if (authState?.isAuthenticated) {
      const idToken: IDToken = authState?.idToken
      const accessToken: AccessToken = authState?.accessToken
      ssoLogin({
        idToken: idToken?.idToken,
        accessToken: accessToken?.accessToken,
      })
    }
    // eslint-disable-next-line
  }, [authState])

  useEffect(() => {
    if (!ssoLoginIsLoading) {
      adoptLegitSession(LEGIT_SERVICE_CONFIG, ALL_REQUESTED_PERMISSIONS)
    }
    // eslint-disable-next-line
  }, [ssoLoginIsLoading])

  const onUsernameChange = (value: string) => {
    setUsernameError(isPrimitiveEmpty(value))
    setUsername(value)
  }

  const handleIdpCheck = () => {
    setUsernameError(isPrimitiveEmpty(username))

    if (!isPrimitiveEmpty(username)) {
      idpCheck({ userName: username })
    }
  }

  return (
    <>
      {ssoError && (
        <>
          <div className={layoutStyles.offsetBar}>
            <MessageCenter errorMessages={[SSO_ERROR_MESSAGE]} onDismissErrors={clearSsoLogin} />
          </div>
        </>
      )}
      {(isNil(loginTypeResponse) ||
        loginTypeResponse?.loginType === '' ||
        loginTypeResponse?.loginType === 'LEGACY') && (
        <Card centered={true} className={styles.loginContainer}>
          <Card.Content>
            <Card.Header textAlign="center">
              <Header as="h1" color="blue">
                Sign In
              </Header>
            </Card.Header>
          </Card.Content>
          <Card.Content className={styles.loginContent}>
            {(isNil(loginTypeResponse) || loginTypeResponse?.loginType === '') && (
              <ApexUsernameCheck
                username={username}
                usernameError={usernameError}
                handleChangeUsername={onUsernameChange}
                handleSubmit={handleIdpCheck}
                checkingUserLoggedIn={ssoLoginIsLoading}
                isLoginTypeLoading={isLoginTypeLoading}
              />
            )}
            {loginTypeResponse?.loginType === 'LEGACY' && (
              <ApexLegacyLogin defaultUsername={username} loggingIn={isLegacyLoginLoading} />
            )}
          </Card.Content>
        </Card>
      )}
      {loginTypeResponse?.loginType === 'OKTA' && !authState?.isAuthenticated && <OktaLogin username={username} />}
    </>
  )
}

const mapStateToProps = (state: RootState) => {
  const legitAuthState = (state as any)?.APEX_LEGIT_AUTHORIZATION?.status
  return {
    isLegacyLoginLoading: legit.selectors.isSessionLoading(state),
    loginTypeResponse: ApexAuth.loginType.selectors.loginType(state),
    isLoginTypeLoading: ApexAuth.loginType.selectors.isLoginTypeLoading(state),
    ssoLoginIsLoading: ApexAuth.loginSso.selectors.isSsoLoginLoading(state),
    ssoError: ApexAuth.loginSso.selectors.error(state),
    failedLegitAuth: legitAuthState === 'APEX_LEGIT_AUTHORIZATION/FAILURE',
  }
}

const mapDispatchToProps = {
  adoptLegitSession: legit.adoptExistingSession,
  idpCheck: ApexAuth.loginType.actions.getLoginType.request,
  ssoLogin: ApexAuth.loginSso.actions.createSsoLogin.request,
  clearSsoLogin: ApexAuth.loginSso.actions.clearSsoLogin,
}

export const ApexLogin = connect(mapStateToProps, mapDispatchToProps)(ApexLoginComponent)
